import { FC, ReactNode, useRef, useState } from 'react'

import AdomikLogo from 'src/assets/images/adomik-logo-blue-vect.png'
import AccountProfileButtonContainer from 'src/components/data/containers/AccountProfileButtonContainer/AccountProfileButtonContainer'
import ProductPage from 'src/components/ui/atoms/ProductPage/ProductPage'
import usePrinter from 'src/hooks/usePrinter'
import useProductPage from 'src/hooks/useProductPage'
import useQueryParams from 'src/hooks/useQueryParams'
import { cn } from 'src/lib/cn'
import { Product, SHARE_LINK_ALLOWED_PRODUCTS, SubProduct } from 'src/utils/product'
import SeparatorVertical from '../../atoms/SeparatorVertical'
import PageHeaderDashboardMenu from '../PageHeaderDashboardMenu'
import PageHeaderListDashboards from '../PageHeaderListDashboards'
import PageHeaderSettings from '../PageHederSettings'
import PageLoadingHandler from '../PageLoadingHandler'
import ModalExportDashboard from './ModalExportDashboard'
import PageHeaderProductTitle from './PageHeaderProductTitle'
import { PageHeaderProvider } from './PageHeaderProvider'
import PageHeaderSaveAsDashboard from './PageHeaderSaveAsDashboard'
import PageHeaderSaveDashboard from './PageHeaderSaveDashboard'
import PageHeaderShareDashboard from './PageHeaderShareDashboard'

import * as styles from './PageHeader.scss'

interface PageHeaderProps {
  printModeAllowed?: boolean
  componentsCodes?: { exportDashboard: string }
  title?: string | ReactNode
}

const SUBPRODUCT_WITHOUT_SAVE_DASHBOARDS = [
  SubProduct.MarketAlerts,
  SubProduct.MarketAlertsV2,
  SubProduct.ChatBot,
] // All paths that match with subProduct

const PRODUCT_WITHOUT_SAVE_DASHBOARDS = [Product.PRICE, Product.TROUBLESHOOT, Product.PbsYield] // All paths that match /troubleshoot/, /price/

const PageHeader: FC<PageHeaderProps> = ({ printModeAllowed, componentsCodes, title }) => {
  const { printMode } = usePrinter()
  const page = useProductPage()
  const { queryParams } = useQueryParams()
  const displaySaveDashboardContainer =
    !PRODUCT_WITHOUT_SAVE_DASHBOARDS.includes(page.product as Product) &&
    !SUBPRODUCT_WITHOUT_SAVE_DASHBOARDS.includes(page.code as SubProduct) &&
    Object.keys(page).length

  const exportId = queryParams.export_id

  const headerLeftRef = useRef<HTMLDivElement | null>(null)

  const [isExportSchedulingOpen, setIsExportSchedulingOpen] = useState(false)

  const showMenu = !exportId && Boolean(displaySaveDashboardContainer) && !printMode

  const canShareDashboard = SHARE_LINK_ALLOWED_PRODUCTS.includes(page.product)

  return (
    <PageHeaderProvider
      exportDashboardComponentCode={componentsCodes?.exportDashboard}
      isExportSchedulingOpen={isExportSchedulingOpen}
      setIsExportSchedulingOpen={setIsExportSchedulingOpen}
      printModeAllowed={Boolean(printModeAllowed)}
    >
      <PageLoadingHandler />
      <header className={cn(styles.PageHeader, { [styles.PageHeaderPrintMode]: printMode })}>
        <div className={cn(styles.leftContent, 'gap-2')} ref={headerLeftRef}>
          <img className={styles.Logo} alt="logo" src={AdomikLogo} />
          {/* Title when no product provider is selected */}
          {!displaySaveDashboardContainer &&
            (typeof title === 'string' ? <ProductPage title={title} /> : title)}

          {/* Title with actual dashboard name in print mode */}
          {printMode && Boolean(displaySaveDashboardContainer) && (
            <PageHeaderProductTitle subProductTitle={page.subProductTitle} />
          )}

          {showMenu && (
            <>
              <PageHeaderProductTitle subProductTitle={page.subProductTitle} />
              <PageHeaderListDashboards />
              <PageHeaderDashboardMenu />
              {componentsCodes?.exportDashboard && (
                <ModalExportDashboard
                  isOpen={isExportSchedulingOpen}
                  onClose={() => setIsExportSchedulingOpen(false)}
                  exportDashboardComponentCode={componentsCodes.exportDashboard}
                />
              )}
              <div className="bg-porcelain flex h-[25px] items-center gap-2 rounded-sm px-2">
                <PageHeaderSaveAsDashboard />
                <SeparatorVertical withoutMargin className="!h-[17px]" />
                <PageHeaderSaveDashboard />
                {canShareDashboard && (
                  <>
                    <SeparatorVertical withoutMargin className="!h-[17px]" />
                    <PageHeaderShareDashboard />
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <div className={styles.rightContent}>
          <AccountProfileButtonContainer />
          {!printMode && <div className="w-[47px]" />}
          <PageHeaderSettings />
        </div>
      </header>
    </PageHeaderProvider>
  )
}

export default PageHeader
